import React from "react";
import { Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import FilePicker from "components/FilePicker";
import useConsent from "hooks/useConsent";
import { useSnackbar } from "hooks/useSnackbar";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { removeFtuePacks, uploadBulkFtuePacks } from "services/ftuePackService";

const useStyles = makeStyles((theme) => ({
  imagePickerContainer: {
    width: "100%",
    display: "flex",
    flexFlow: "row wrap",
    alignItems: "end",
    gap: theme.spacing(2),
    marginBottom: 20,
  },
  botghostBtnContainer: {
    display: "flex",
    flexFlow: "row wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  uploadBtnContainer: {
    height: 38,
    display: "flex",
    gap: 10,
  },
}));

const FTUEPackManagement = () => {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = React.useState(false);
  const [fileDetails, setFileDetails] = React.useState({
    file: null,
    fileType: ".csv, .xlsx, .xls",
  });

  const onSelectFile = (file: any) => {
    if (!file) {
      setFileDetails((st) => ({ ...st, file: null }));
      return;
    }
    setFileDetails((st) => ({ ...st, file }));
  };

  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const _openConsent = async (type) => {
    updateDetails({ type });
    openConsent();
  };

  const clearFtuePacks = async (type) => {
    closeConsent();
    if (type !== "clear") {
      showSnackbar("Invalid type", "error");
      return;
    }
    setLoading(true);
    try {
      await removeFtuePacks();
      showSnackbar(`All FTUE packs cleared successfully`, "success");
    } catch (error) {
      console.error("Error clearing FTUE Packs:", error);
      showSnackbar("Failed to clear FTUE Packs", "error");
    } finally {
      setLoading(false);
      closeConsent();
    }
  };

  const getBtnText = () => {
    return constentDetails.type === "Upload"
      ? "Upload FTUE Pack File"
      : `Clear All FTUE Packs`;
  };

  const getMessageText = () => {
    return constentDetails.type === "Upload"
      ? "This operation will upload the FTUE Packs File and update/overwrite the FTUE Packs"
      : `This operation will delete all FTUE Packs. Are you sure you want to proceed?`;
  };

  const uploadFtuePacks = async () => {
    try {
      setLoading(true);
      const response = await uploadBulkFtuePacks(fileDetails.file);
      if (!response.status) {
        throw new Error(response?.message || "Upload FTUE packs failed.");
      }
      showSnackbar(response.message);
    } catch (error) {
      console.error(error);
      showSnackbar(error.message, "error");
    } finally {
      setFileDetails((st) => ({ ...st, file: null }));
      setLoading(false);
      closeConsent();
    }
  };

  const downloadTemp = () => {
    try {
      const link = document.createElement("a");
      link.href = "ftue_template.xlsx";
      link.download = "ftue_template.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      showSnackbar(error.message || "Download failed", "error");
    }
  };

  return (
    <ContentShell title={`FTUE Pack Management`} loading={loading}>
      <Box className={classes.botghostBtnContainer}>
        <CustomButton
          startIcon={<RestartAltIcon />}
          onClick={() => _openConsent("clear")}
        >{`Clear All FTU Packs`}</CustomButton>
      </Box>

      <Box className={classes.imagePickerContainer}>
        <FilePicker
          label="Select FTUE Packs Excel"
          name="ftue"
          fileType={fileDetails.fileType}
          value={fileDetails.file}
          showPreview={false}
          onFileSelect={onSelectFile}
        />
      </Box>
      <Box className={classes.uploadBtnContainer}>
        <CustomButton
          disabled={!fileDetails.file}
          startIcon={<CloudUploadIcon />}
          onClick={() => _openConsent("Upload")}
        >
          Upload
        </CustomButton>
        <CustomButton startIcon={<ArrowDownwardIcon />} onClick={downloadTemp}>
          Download FTUE Pack Example Template
        </CustomButton>
      </Box>

      <DeleteConsentDialog
        btnText={getBtnText()}
        message={getMessageText()}
        itemName={constentDetails.itemName}
        open={isOpenConsent}
        onClose={closeConsent}
        onConfirm={() =>
          constentDetails.type === "Upload"
            ? uploadFtuePacks()
            : clearFtuePacks("clear")
        }
      />
    </ContentShell>
  );
};

export default FTUEPackManagement;
